import { AnyKindOfDictionary } from "lodash";
import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";

// Define interface for component props
interface CustomPieChartProps {
  // Required props
  data: Array<{
    label: string;
    value: number;
  }>;

  // Optional customization props
  height?: number;
  width?: string | number;
  outerRadius?: number;
  colors?: string[];
  showLabels?: boolean;
  showTooltip?: boolean;
  startAngle?: number;
  endAngle?: number;
  decimalPlaces?: number;
  showInternalLegend?: boolean; // New prop to control legend rendering
}

// Helper function to format numbers
const formatNumber = (value: any, decimalPlaces = 1): string => {
  if (typeof value === "number") {
    return value.toFixed(decimalPlaces);
  }
  return String(value);
};

// Function to transform data for rendering (use absolute values)
const prepareDataForRendering = (
  data: Array<{ label: string; value: number }>,
) => {
  return data.map((item) => ({
    ...item,
    originalValue: item.value, // Store the original value
    value: Math.abs(item.value), // Use absolute value for rendering
  }));
};

// Function to generate dynamic colors
const getColor = (index: number) => {
  // Using HSL to generate evenly distributed colors
  // 360 * golden ratio ensures good distribution
  const hue = (index * 137.5) % 360;
  return `hsl(${hue}, 65%, 55%)`;
};

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
  payload,
  index,
  decimalPlaces = 1,
}: any) => {
  // Determine if this is a small slice (less than 8% of the total)
  const isSmallSlice = percent < 0.08;

  // For larger slices, position label within the slice
  // For smaller slices, position outside the pie
  const radius = isSmallSlice
    ? outerRadius * 1.2
    : innerRadius + (outerRadius - innerRadius) * 0.6;

  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  // Always use white text for visibility against dark background
  const textColor = "white";
  const textAnchor = isSmallSlice ? (x > cx ? "start" : "end") : "middle";

  // Use original value (possibly negative) for display
  const originalValue =
    payload.originalValue !== undefined ? payload.originalValue : value;

  // Format value to specified decimal places
  const formattedValue = formatNumber(originalValue, decimalPlaces);

  return (
    <>
      <text
        x={x}
        y={y}
        fill={textColor}
        textAnchor={textAnchor}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="600"
      >
        {formattedValue}
      </text>
    </>
  );
};

export const CustomLegend = (props: any) => {
  const { payload, decimalPlaces = 1 ,isImage=false} = props;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: 0,
        margin: 0,
        gap: isImage ? "15px" : "10px",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {payload.map((entry: any, index: AnyKindOfDictionary) => {
        // Use the label from payload instead of the value
        const label = entry.payload?.label || "";

        return (
          <div
            key={`item-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "6px",
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                backgroundColor: entry.color,
                marginRight: 4,
                borderRadius: "50%",
                marginTop: isImage ? "14px" : "",
              }}
            />
            <span
              style={{ color: "rgba(255, 255, 255, 0.8)", fontSize:  isImage ? "14px" : "12px" }}
            >
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const CustomTooltip = ({ active, payload, decimalPlaces = 1 }: any) => {
  if (active && payload && payload.length) {
    // Use original value (possibly negative) for display
    const originalValue =
      payload[0].payload?.originalValue !== undefined
        ? payload[0].payload.originalValue
        : payload[0].value;

    // Format tooltip value
    const formattedValue = formatNumber(originalValue, decimalPlaces);
    return (
      <div
        style={{
          backgroundColor: "#363441",
          padding: "4px 8px",
          borderRadius: "5px",
        }}
      >
        <p style={{ color: "white", margin: "0", fontSize: "10px" }}>
          {" "}
          {formattedValue}
        </p>
      </div>
    );
  }
  return null;
};

// Prepare legend data for external rendering
export const prepareLegendData = (data: any[], colors?: string[]) => {
  return data.map((item, index) => ({
    color: colors ? colors[index % colors.length] : getColor(index),
    value: item.value,
    payload: { label: item.label },
  }));
};

const CustomePieChart: React.FC<CustomPieChartProps> = ({
  data,
  height = "100%",
  width = "100%",
  outerRadius = 100,
  colors,
  showLabels = true,
  showTooltip = true,
  startAngle = 90,
  endAngle = -270,
  decimalPlaces = 1,
}) => {
  // Transform data to use absolute values for rendering while preserving original values
  const renderData = prepareDataForRendering(data);

  return (
    <ResponsiveContainer width={width} minHeight={height} className="mt-[25px]">
      <PieChart>
        <Pie
          data={renderData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={
            showLabels
              ? (props) => renderCustomizedLabel({ ...props, decimalPlaces })
              : undefined
          }
          outerRadius={outerRadius}
          fill="#8884d8"
          dataKey="value"
          nameKey="label"
          startAngle={startAngle}
          endAngle={endAngle}
          stroke="none"
          isAnimationActive={true}
          // onClick={onSliceClick}
        >
          {renderData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={colors ? colors[index % colors.length] : getColor(index)}
              stroke="none"
              strokeWidth={0}
              style={{ outline: "none" }}
            />
          ))}
        </Pie>
        {showTooltip && (
          <Tooltip
            content={(props) => (
              <CustomTooltip {...props} decimalPlaces={decimalPlaces} />
            )}
          />
        )}
      </PieChart>
    </ResponsiveContainer>
  );
};

export default CustomePieChart;
